import type { Ingredient, Requirement } from './types.js';

function lookup(ingredients: ReadonlyMap<string, Ingredient>, id: string): Ingredient {
  const ingredient = ingredients.get(id);
  if (!ingredient) {
    throw new Error(`Unknown ingredient "${id}" while applying the cupboard check`);
  }
  return ingredient;
}

/**
 * Remove ingredients the household already has.
 *
 * Only cupboard staples are eligible. A non-staple is never excluded, whatever
 * the pantry map says — perishables are re-bought weekly, and dropping milk on
 * the strength of a stale "got it" is how a family ends up with no milk.
 */
export function applyPantry(
  reqs: Requirement[],
  ingredients: ReadonlyMap<string, Ingredient>,
  pantry: Record<string, boolean>,
): Requirement[] {
  return reqs.filter((req) => {
    const ingredient = lookup(ingredients, req.ingredientId);
    if (!ingredient.isCupboardStaple) return true;
    return pantry[req.ingredientId] !== true;
  });
}

/** The staples this plan needs — the only rows the cupboard-check screen shows. */
export function stapleIdsNeeded(
  reqs: Requirement[],
  ingredients: ReadonlyMap<string, Ingredient>,
): string[] {
  const ids = reqs
    .filter((req) => lookup(ingredients, req.ingredientId).isCupboardStaple)
    .map((req) => req.ingredientId);
  return [...new Set(ids)];
}
